import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatMoneyDecimalDisplay,
  formatMoneyInputTyping,
  parseMoneyDecimalInput,
  pkrAmountShortLabel,
} from "@/lib/formatPkrAmount";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState, type ReactNode } from "react";

type Size = "sm" | "md";

type InputProps = {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  showShortLabel?: boolean;
  size?: Size;
  id?: string;
};

export function MoneyAmountInput({
  value,
  onChange,
  disabled,
  className,
  inputClassName,
  showShortLabel = true,
  size = "md",
  id,
}: InputProps) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState("");

  const shortLabel = useMemo(() => {
    if (!showShortLabel || value <= 0) return "";
    return pkrAmountShortLabel(value);
  }, [showShortLabel, value]);

  useEffect(() => {
    if (!focused) {
      setText(value > 0 ? formatMoneyDecimalDisplay(value) : "");
    }
  }, [focused, value]);

  const isCompact = size === "sm";

  return (
    <div className={cn("flex w-full flex-col gap-0.5", isCompact ? "items-end" : "items-stretch", className)}>
      {shortLabel ? (
        <span
          className={cn(
            "font-medium text-primary tabular-nums",
            isCompact ? "text-[10px]" : "text-sm",
          )}
        >
          {shortLabel}
        </span>
      ) : null}
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        className={cn(
          "glass-input tabular-nums",
          isCompact ? "h-8 min-w-[9rem] text-right text-xs" : "h-10 w-full text-sm",
          inputClassName,
        )}
        value={focused ? text : value > 0 ? formatMoneyDecimalDisplay(value) : ""}
        placeholder="0"
        onFocus={() => {
          setFocused(true);
          setText(value > 0 ? formatMoneyDecimalDisplay(value) : "");
        }}
        onBlur={() => {
          setFocused(false);
          setText(value > 0 ? formatMoneyDecimalDisplay(value) : "");
        }}
        onChange={(e) => {
          const next = formatMoneyInputTyping(e.target.value);
          setText(next);
          onChange(parseMoneyDecimalInput(next));
        }}
      />
    </div>
  );
}

type FieldProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  hint?: ReactNode;
  className?: string;
  labelClassName?: string;
};

export function MoneyAmountField({
  label,
  value,
  onChange,
  disabled,
  required,
  id,
  hint,
  className,
  labelClassName,
}: FieldProps) {
  const shortLabel = useMemo(() => (value > 0 ? pkrAmountShortLabel(value) : ""), [value]);

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-x-2 min-h-6">
        {required ? (
          <Label htmlFor={id} className={cn("text-xs text-muted-foreground whitespace-nowrap", labelClassName)}>
            {label}
            <span className="text-destructive ml-0.5">*</span>
          </Label>
        ) : (
          <Label htmlFor={id} className={cn("text-xs text-muted-foreground whitespace-nowrap", labelClassName)}>
            {label}
          </Label>
        )}
        {shortLabel ? (
          <span className="shrink-0 text-[11px] font-medium leading-none text-primary tabular-nums">
            {shortLabel}
          </span>
        ) : null}
      </div>
      <MoneyAmountInput
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        showShortLabel={false}
        size="md"
      />
      {hint}
    </div>
  );
}
